import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Injector,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { ControlValueAccessorConnector } from '../control-value-accessor-connector';
import { LingInputTypes } from '../ling-input-types';
import {
  getFormatted,
  getProcessed,
  processValueOnInputEvent,
  triggerInputEvent,
} from '../inputs-utils';
import { numDigits } from '../../../../core/utils';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ling-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: InputComponent,
      multi: true,
    },
  ],
})
export class InputComponent
  extends ControlValueAccessorConnector
  implements AfterViewChecked, OnChanges
{
  @ViewChild('input') inputRef!: ElementRef;
  @ViewChild('orControl') orControlRef!: ElementRef;
  @ViewChildren('inputBox') inputBoxes!: QueryList<ElementRef>;
  @Output() valueChange = new EventEmitter();
  @Output() removeButtonPress = new EventEmitter();
  @Output() editButtonPress = new EventEmitter();
  @Input() removeButton = false;
  @Input() isEditButton = false;
  @Input() editButtonInfo = '';
  @Input() info = '';
  @Input() label = '';
  @Input() orOptions: { true: string; false: string } = {
    true: 'Yes',
    false: 'No',
  };
  @Input() autocomplete = '';
  @Input() asBoxes = false;
  @Input() boxesLength = 1;
  @Input() selectOptions: { value: any; text: string }[] = [];
  @Input() type: LingInputTypes = 'text';
  @Input() counterLimit?: number;
  @Input() counterUseDefaultPlaceholder?: boolean = false;
  @Input() counterHidePadding?: boolean = false;
  @Input() counterLabel? = '';
  @Input() currency? = '';
  @Input() placeholder = '';
  @Input() textAreaHeight?: string;
  @Input() maxLength = 2000;
  @Input() maxTextAreaLength = 2000;
  @Input() borderRadius = '';

  private _disabledInput = false;
  private _isPendingValue = false;

  isPasswordVisible = false;
  processValueOnInputEvent = processValueOnInputEvent;
  isFocused = false;

  getFormatted = getFormatted;

  // Cannot use [disabled] directive on input because it's overrode by forms module directive.
  @Input() set disabledInput(isDisabled: boolean) {
    this._disabledInput = isDisabled;
    if (this.inputRef?.nativeElement) {
      if (isDisabled) {
        this.inputRef.nativeElement.setAttribute('disabled', true);
      } else {
        this.inputRef.nativeElement.removeAttribute('disabled');
      }
    }
  }

  get disabledInput() {
    return this._disabledInput;
  }

  get isSearch(): boolean {
    return this.type === 'search';
  }

  @HostBinding('tabIndex') get tabIndex() {
    return this.disabledInput ? -1 : 0;
  }

  @HostListener('focusin', ['$event'])
  onFocusIn(event: FocusEvent) {
    if (event.target !== this.removeInputButtonElement && !this.disabledInput) {
      if (this.asBoxes && !this.isFocused) {
        this.focusFirstUnfilledBox();
      }

      if (this.inputRef && !this.inputRef?.nativeElement?.focused) {
        this.inputRef?.nativeElement?.focus();
      }

      this.isFocused = true;
    }
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: any) {
    if (!this.elementRef.nativeElement.contains(target) && this.isFocused) {
      this.isFocused = false;
      this.isPasswordVisible = false;
      this.onTouched();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Tab': {
        this.onTabPressed();
        break;
      }
      case 'Enter': {
        this.onEnterPressed();
        break;
      }
    }
  }

  constructor(
    injector: Injector,
    private elementRef: ElementRef,
    private translateService: TranslateService
  ) {
    super(injector, translateService);
  }

  ngAfterViewChecked() {
    this.disabledInput = this._disabledInput;
    if (this._isPendingValue) {
      if (this.type === 'counter' && this.inputRef) {
        this.inputRef.nativeElement.value = getFormatted(
          this.type,
          this.control.value,
          {
            counterLimit: this.counterLimit,
            counterHidePadding: this.counterHidePadding,
          }
        );
        this._isPendingValue = false;
      }
    }
  }

  override ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (!this.counterUseDefaultPlaceholder) {
      this.placeholder =
        this.type === 'counter' && this.counterLimit
          ? ''.padStart(numDigits(this.counterLimit), '0')
          : this.placeholder;
    }
  }

  get boxes(): any[] {
    return this.asBoxes ? new Array(this.boxesLength).fill(1) : [];
  }

  getBoxValue(index: number): string {
    return String(this.control.value).at(index) ?? '';
  }

  get removeInputButtonElement(): HTMLButtonElement | null {
    return (this.elementRef.nativeElement as HTMLElement).querySelector(
      '#removeInputButton'
    );
  }

  get isRequired(): boolean {
    return this.control.hasValidator(Validators.required);
  }

  get actualInputType(): string {
    switch (this.type) {
      case 'counter':
        return 'text';
      case 'email':
        return 'email';
      case 'color':
        return 'text';
      case 'password':
        return this.isPasswordVisible && this.isFocused ? 'text' : 'password';
      default:
        return 'text';
    }
  }

  override writeValue(value: any) {
    super.writeValue(value);
    if (!this.inputRef) {
      this._isPendingValue = true;
    }
    if (this.type === 'counter' && this.inputRef) {
      this.inputRef.nativeElement.value = getFormatted(
        this.type,
        this.control.value,
        {
          counterLimit: this.counterLimit,
          counterHidePadding: this.counterHidePadding,
        }
      );
      this._isPendingValue = false;
    }
  }

  clearSearch(): void {
    this.onTouched();
    this.inputRef.nativeElement.value = '';
    this.control.setValue('');
    this.onChange('');
    this.valueChange.emit('');
    triggerInputEvent('input', this.inputRef.nativeElement);
  }

  onLineTextInput(event: any) {
    if (this.type !== 'password' && this.type !== 'number') {
      event.target.value = event.target.value?.trim();
      this.onChange(event.target.value);
    }
    if (
      this.type === 'color' &&
      event.target.value &&
      typeof event.target.value === 'string' &&
      event.target.value.length === 4 &&
      event.target.value.startsWith('#')
    ) {
      const value = event.target.value;
      event.target.value = `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
      this.onChange(event.target.value);
    }
    this.valueChange.emit(event.target.value);
    this.onTouched();
  }

  blockCounterChars(event: any): any {
    if (this.currency) {
      ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();
    } else {
      ['e', 'E', '+', '-', '.', ','].includes(event.key) &&
        event.preventDefault();
    }
  }

  onTabPressed(): void {
    if (this.isFocused) {
      this.isFocused = false;
      this.onTouched();
    }
  }

  onEnterPressed(): void {
    if (this.isFocused && this.type === 'or') {
      this.onOrControlOptionClick(
        this.orControlRef.nativeElement,
        !this.control.value
      );
    }
  }

  focusFirstUnfilledBox(): void {
    const focusedIndex =
      String(this.control.value).length &&
      String(this.control.value).length < this.boxesLength
        ? String(this.control.value).length
        : 0;
    this.inputBoxes.toArray().at(focusedIndex)?.nativeElement.focus();
  }

  onBoxClick(index: number, event: any): void {
    event.preventDefault();
    if (this.inputBoxes.toArray().at(index)?.nativeElement.value) {
      return;
    }
    this.focusFirstUnfilledBox();
  }

  onBoxInput(index: number, event: any): void {
    if (event.inputType === 'insertLineBreak') {
      return;
    } else {
      event.preventDefault();
    }
    const incomingValue: string = event.data ?? '';
    let newControlValue = this.control.value;
    let nextFocusedIndex = index;
    const processedIncoming = getProcessed(this.type, incomingValue);

    if (processedIncoming !== undefined) {
      const processedIncomingAsString = String(
        getProcessed(this.type, incomingValue)
      );
      const processedIncomingAsArray = Array.from(processedIncomingAsString);

      if (processedIncomingAsString.length) {
        const valueAsArray = Array.from(String(this.control.value));
        processedIncomingAsArray.forEach((char, newIndex) => {
          const editedIndex = index + newIndex;
          if (editedIndex < this.boxesLength) {
            valueAsArray[editedIndex] = char;
          }
        });
        newControlValue = getProcessed(this.type, valueAsArray.join(''));
        if (processedIncomingAsString.length > 1) {
          if (String(newControlValue).length < this.boxesLength) {
            nextFocusedIndex = String(newControlValue).length;
          }
          if (String(newControlValue).length === this.boxesLength) {
            nextFocusedIndex = String(newControlValue).length - 1;
          }
        } else {
          nextFocusedIndex = index < this.boxesLength ? index + 1 : index;
        }
      } else if (processedIncomingAsString.length === 0) {
        newControlValue = getProcessed(
          this.type,
          String(this.control.value).slice(0, -1)
        );
        nextFocusedIndex = index === 0 ? 0 : index - 1;
      }
    }
    this.onChange(getProcessed(this.type, newControlValue));
    setTimeout(() => {
      this.inputBoxes.toArray().at(nextFocusedIndex)?.nativeElement.focus();
    });
  }

  onOrControlOptionClick(checkbox: HTMLInputElement, newValue: boolean) {
    this.onTouched();
    if (newValue === this.control.value) {
      return;
    }
    this.control.setValue(newValue);
    this.valueChange.emit(newValue);
  }

  onPasswordVisibilityToggle(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
    this.inputRef.nativeElement.focus();
    setTimeout(() => {
      const length = this.inputRef.nativeElement.value.length;
      this.inputRef.nativeElement.setSelectionRange(length, length);
    });
  }

  onRemoveButtonPressed(): void {
    this.removeButtonPress.emit();
  }

  onCounterInput(event: any): void {
    if (!this.currency) {
      processValueOnInputEvent(this.control, this.type, this.onChange, event, {
        counterLimit: this.counterLimit,
        counterHidePadding: this.counterHidePadding,
      });
    } else {
      if (
        event.target.value.indexOf('.') >= 0 &&
        event.target.value.indexOf('.') < event.target.value.length - 1
      ) {
        event.target.value = Number(
          event.target.value.substring(0, event.target.value.indexOf('.')) +
            event.target.value.substring(
              event.target.value.indexOf('.'),
              event.target.value.indexOf('.') + 3
            )
        );
      }
      if (this.counterLimit && event.target.value > this.counterLimit) {
        event.target.value = this.counterLimit;
      }
      this.onChange(Number(event.target.value));
    }
  }
}
